import { AccumulativeShadows, RandomizedLight } from "@react-three/drei";
import {
  DEFAULT_SCENE_OPTICS_CONFIG,
  DEFAULT_SHADOW_CONFIG,
} from "@/lib/laser-physics";

/**
 * 增强水晶光照系统配置 - 多光源复合系统，增强通透感和真实感
 * 使用 DEFAULT_SCENE_OPTICS_CONFIG 作为主光源位置配置源
 */
const ENHANCED_CRYSTAL_LIGHTING_CONFIG = {
  // 环境光 - 提供基础照明
  ambientLight: {
    intensity: 0.6 * Math.PI, // 略微增强环境光
    color: "#ffffff",
  },

  // 主聚光灯 - 使用统一的光源位置配置，确保与阴影系统一致
  primarySpotLight: {
    position: DEFAULT_SCENE_OPTICS_CONFIG.lightSource,
    angle: 0.12, // 更聚焦的光束
    penumbra: 0.8, // 柔和边缘
    intensity: 1.2, // 增强主光源强度
    color: "#FFF8DC", // 3200K 暖色主光源
    decay: 0,
  },

  // 辅助聚光灯 - 增强水晶内部层次
  secondarySpotLight: {
    position: [-3, 4, -8] as [number, number, number],
    angle: 0.2,
    penumbra: 1.0,
    intensity: 0.7,
    color: "#E6F3FF", // 6000K 冷色辅助光
    decay: 0,
  },

  // 多角度点光源阵列 - 模拟水晶内部折射和反射
  crystalRefractionLights: [
    {
      position: [-8, 6, 6] as [number, number, number],
      intensity: 0.8,
      color: "#ffffff",
      distance: 15, // 物理光衰减
      decay: 1,
    },
    {
      position: [8, -2, -8] as [number, number, number],
      intensity: 0.6,
      color: "#F0F8FF", // 轻微蓝色调
      distance: 12,
      decay: 1,
    },
    {
      position: [0, -6, 6] as [number, number, number],
      intensity: 0.5,
      color: "#FFF5EE", // 轻微暖色调
      distance: 10,
      decay: 1,
    },
  ],

  // 边缘背光 - 增强轮廓和通透感
  rimLight: {
    position: [0, 0, -12] as [number, number, number],
    intensity: 0.4,
    color: "#E0E6FF", // 冷色背光
    distance: 20,
    decay: 1,
  },
};

/**
 * 阴影系统配置 - 与光照系统共享位置参数
 */
const SHADOW_CONFIG = {
  ...DEFAULT_SHADOW_CONFIG,
  initializationDelay: 100, // 快速初始化以提升首页体验
};

/**
 * 增强水晶光照阴影系统组件 - 多光源复合系统
 * 专为水晶材质优化的复杂光照配置，增强通透感和真实感
 * 确保主光源位置与阴影方向完全匹配
 */
export function LightingShadowSystem() {
  return (
    <>
      {/* === 基础环境光照 === */}
      <ambientLight
        intensity={ENHANCED_CRYSTAL_LIGHTING_CONFIG.ambientLight.intensity}
        color={ENHANCED_CRYSTAL_LIGHTING_CONFIG.ambientLight.color}
      />

      {/* === 主要聚光灯系统 === */}
      {/* 主聚光灯 - 3200K暖色主光源 */}
      <spotLight
        position={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.position}
        angle={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.angle}
        penumbra={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.penumbra}
        intensity={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.intensity}
        color={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.color}
        decay={ENHANCED_CRYSTAL_LIGHTING_CONFIG.primarySpotLight.decay}
        castShadow
      />

      {/* 辅助聚光灯 - 6000K冷色辅助光 */}
      <spotLight
        position={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.position}
        angle={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.angle}
        penumbra={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.penumbra}
        intensity={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.intensity}
        color={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.color}
        decay={ENHANCED_CRYSTAL_LIGHTING_CONFIG.secondarySpotLight.decay}
      />

      {/* === 水晶折射模拟点光源阵列 === */}
      {ENHANCED_CRYSTAL_LIGHTING_CONFIG.crystalRefractionLights.map((light, index) => (
        <pointLight
          key={`refraction-light-${index}`}
          position={light.position}
          intensity={light.intensity}
          color={light.color}
          distance={light.distance}
          decay={light.decay}
        />
      ))}

      {/* === 边缘背光系统 === */}
      <pointLight
        position={ENHANCED_CRYSTAL_LIGHTING_CONFIG.rimLight.position}
        intensity={ENHANCED_CRYSTAL_LIGHTING_CONFIG.rimLight.intensity}
        color={ENHANCED_CRYSTAL_LIGHTING_CONFIG.rimLight.color}
        distance={ENHANCED_CRYSTAL_LIGHTING_CONFIG.rimLight.distance}
        decay={ENHANCED_CRYSTAL_LIGHTING_CONFIG.rimLight.decay}
      />

      {/* === 阴影系统 === */}
      {/* 与主光源位置统一的高质量阴影 */}
      <AccumulativeShadows
        temporal
        frames={SHADOW_CONFIG.accumulative.frames}
        color={SHADOW_CONFIG.accumulative.color}
        colorBlend={SHADOW_CONFIG.accumulative.colorBlend}
        toneMapped={SHADOW_CONFIG.accumulative.toneMapped}
        alphaTest={SHADOW_CONFIG.accumulative.alphaTest}
        opacity={SHADOW_CONFIG.accumulative.opacity}
        scale={SHADOW_CONFIG.accumulative.scale}
        position={DEFAULT_SCENE_OPTICS_CONFIG.effectPlane}
      >
        <RandomizedLight
          amount={SHADOW_CONFIG.randomizedLight.amount}
          radius={SHADOW_CONFIG.randomizedLight.radius}
          ambient={SHADOW_CONFIG.randomizedLight.ambient}
          position={DEFAULT_SCENE_OPTICS_CONFIG.lightSource}
          bias={SHADOW_CONFIG.randomizedLight.bias}
        />
      </AccumulativeShadows>
    </>
  );
}
